// Radha — weekly digest subscriptions, surfaced in Settings. Sending needs a
// Resend API key (RESEND_API_KEY) — 503 with a clear message until it's set,
// same as every other integration this session. Preview always works since
// it's pure text generation with no external dependency.
import React, { useState } from "react";

import {
  useDigestSubscriptions, useAddDigestSubscription, useRemoveDigestSubscription,
  usePreviewDigest, useSendDigestNow, reportingErrorMessage,
} from "../hooks/useReporting";
import { fmtDate } from "../lib/money";

export function DigestSettings({ canWrite }: { canWrite: boolean }) {
  const { data: subs, isLoading } = useDigestSubscriptions();
  const add = useAddDigestSubscription();
  const remove = useRemoveDigestSubscription();
  const preview = usePreviewDigest();
  const send = useSendDigestNow();
  const [email, setEmail] = useState("");
  const [note, setNote] = useState<{ ok: boolean; text: string } | null>(null);
  const [previewText, setPreviewText] = useState<string | null>(null);

  const addSub = () => {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return;
    setNote(null);
    add.mutate(email, { onSuccess: () => setEmail(""), onError: (e) => setNote({ ok: false, text: reportingErrorMessage(e) }) });
  };

  const doPreview = () => {
    setPreviewText(null);
    preview.mutate(undefined, {
      onSuccess: (text) => setPreviewText(text),
      onError: (e) => setNote({ ok: false, text: reportingErrorMessage(e) }),
    });
  };

  const doSend = () => {
    setNote(null);
    send.mutate(undefined, {
      onSuccess: (r) => setNote({ ok: true, text: `Sent to ${r.sent} of ${r.total} recipient(s)${r.failures.length ? ` — ${r.failures.length} failed` : ""}` }),
      onError: (e) => setNote({ ok: false, text: reportingErrorMessage(e) }),
    });
  };

  return (
    <div style={{ maxWidth: 560, marginTop: 32 }}>
      <div style={{ font: "600 15px Inter", marginBottom: 6 }}>Weekly receivables digest</div>
      <p style={{ color: "var(--fg-muted)", fontSize: 13, margin: "0 0 14px" }}>
        A plain-text summary — outstanding, overdue, DSO, top debtors, week-over-week change — emailed to whoever's
        listed below. Sending needs an email provider configured on the backend; preview always works.
      </p>

      {canWrite && (
        <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
          <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="recipient@company.com"
            style={{ flex: 1, padding: "7px 10px", border: "1px solid var(--border)", borderRadius: 8, fontSize: 13, background: "var(--surface)", color: "var(--fg)" }} />
          <button onClick={addSub} disabled={add.isPending}
            style={{ padding: "7px 14px", background: "var(--accent-purple)", color: "#fff", border: 0, borderRadius: 8, cursor: "pointer", fontSize: 13 }}>
            Add
          </button>
        </div>
      )}

      {!isLoading && (subs ?? []).length > 0 && (
        <div style={{ display: "grid", gap: 4, marginBottom: 12 }}>
          {(subs ?? []).map((s) => (
            <div key={s.id} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
              <span style={{ flex: 1 }}>{s.recipient_email}</span>
              <span style={{ fontSize: 11, color: "var(--fg-faint)" }}>{s.last_sent_at ? `last sent ${fmtDate(s.last_sent_at)}` : "never sent"}</span>
              {canWrite && (
                <button onClick={() => remove.mutate(s.id)}
                  style={{ padding: "1px 6px", fontSize: 11, border: "1px solid var(--border)", borderRadius: 4, background: "none", color: "var(--fg-faint)", cursor: "pointer" }}>
                  remove
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <button onClick={doPreview} disabled={preview.isPending}
          style={{ padding: "6px 12px", fontSize: 12.5, border: "1px solid var(--border)", borderRadius: 7, background: "none", color: "var(--fg-muted)", cursor: "pointer" }}>
          {preview.isPending ? "…" : "Preview"}
        </button>
        {canWrite && (subs ?? []).length > 0 && (
          <button onClick={doSend} disabled={send.isPending}
            style={{ padding: "6px 12px", fontSize: 12.5, border: 0, borderRadius: 7, background: "var(--accent-purple)", color: "#fff", cursor: "pointer" }}>
            {send.isPending ? "Sending…" : "Send now"}
          </button>
        )}
        {note && <span style={{ fontSize: 12.5, color: note.ok ? "var(--status-good)" : "var(--status-critical)" }}>{note.text}</span>}
      </div>

      {previewText && (
        <pre style={{ marginTop: 10, padding: 12, background: "var(--surface-2)", border: "1px solid var(--border)", borderRadius: 8, fontSize: 12, whiteSpace: "pre-wrap", fontFamily: "'DM Mono', ui-monospace, monospace" }}>
          {previewText}
        </pre>
      )}
    </div>
  );
}
